<script setup>
import i18next from 'i18next';
import { computed, nextTick, onBeforeUpdate, onMounted, onUnmounted, ref, watch } from 'vue';
import { fetchData } from '../../../fetchData';
import BreakingNewsItem from './BreakingNewsItem.vue';
import BreakingNewsSkeleton from './BreakingNewsSkeleton.vue';

const props = defineProps({
    scrollToId: {
        type: [String, Number],
        default: null
    }
});

const posts = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref(null);
const currentPage = ref(1);
const hasNextPage = ref(false);
const expandedItemId = ref(null);
const itemRefs = ref({});
const processedScrollToId = ref(false);

const activeHeadline = ref(null);
const headlineRefs = ref({});
const headlinesContainerRef = ref(null);
const headlineButtonRefs = ref({});
const loadMoreTrigger = ref(null);

let observer = null;
const intersectingHeadlines = new Set();
let scrollObserver = null;

const setItemRef = (el, id) => {
    if (el) {
        itemRefs.value[id] = el;
    }
};

const setHeadlineRef = (el, date) => {
    if (el) {
        headlineRefs.value[date] = el;
    }
};

const setHeadlineButtonRef = (el, date) => {
    if (el) {
        headlineButtonRefs.value[date] = el;
    }
};

onBeforeUpdate(() => {
    headlineRefs.value = {};
    headlineButtonRefs.value = {};
    itemRefs.value = {};
});

const groupedPosts = computed(() => {
    return posts.value.reduce((groups, post) => {
        const date = new Date(post.published_at);
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        const groupKey = date.toLocaleDateString(i18next.language, options);

        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }

        groups[groupKey].push(post);

        return groups;
    }, {});
});

const observerCallback = (entries) => {
    entries.forEach((entry) => {
        const dateKey = entry.target.dataset.date;

        if (!dateKey) return;

        if (entry.isIntersecting) {
            intersectingHeadlines.add(dateKey);
        } else {
            intersectingHeadlines.delete(dateKey);
        }
    });

    if (intersectingHeadlines.size > 0) {
        let topMostDate = null;
        let minTop = Infinity;

        intersectingHeadlines.forEach((date) => {
            const el = headlineRefs.value[date];

            if (el) {
                const rect = el.getBoundingClientRect();

                if (rect.top < minTop) {
                    minTop = rect.top;
                    topMostDate = date;
                }
            }
        });
        activeHeadline.value = topMostDate;
    }
};

const setupObserver = () => {
    if (observer) {
        observer.disconnect();
    }

    const options = {
        rootMargin: '0px 0px -50% 0px',
        threshold: 0
    };
    observer = new IntersectionObserver(observerCallback, options);

    Object.values(headlineRefs.value).forEach((el) => {
        if (el) {
            observer.observe(el);
        }
    });
};

const scrollToDate = (date) => {
    const element = headlineRefs.value[date];
    if (element) {
        const yOffset = -150;
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }
};

watch(
    activeHeadline,
    (newVal) => {
        if (newVal && headlineButtonRefs.value[newVal]) {
            const container = headlinesContainerRef.value;
            const button = headlineButtonRefs.value[newVal];
            if (container && button) {
                const containerPadding = 20;
                const scrollLeft = button.offsetLeft - containerPadding;

                container.scrollTo({
                    left: scrollLeft,
                    behavior: 'smooth'
                });
            }
        }
    },
    { flush: 'post' }
);

const scrollAndExpand = () => {
    if (props.scrollToId && !processedScrollToId.value) {
        const postExists = posts.value.some((p) => p.id === props.scrollToId);
        if (postExists) {
            expandedItemId.value = props.scrollToId;

            nextTick(() => {
                const itemRef = itemRefs.value[props.scrollToId];

                if (itemRef?.$el) {
                    itemRef.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    processedScrollToId.value = true;
                }
            });
        }
    }
};

const fetchNews = async (isLoadMore = false) => {
    if (isLoadMore) {
        loadingMore.value = true;
    } else {
        loading.value = true;
        currentPage.value = 1;
    }

    error.value = null;

    const params = new URLSearchParams();

    params.append('page', currentPage.value);
    params.append('limit', 100);
    params.append('sort[by]', 'created_at');
    params.append('sort[order]', 'desc');

    try {
        const { result = {} } = await fetchData(`/api/v1/posts?${params.toString()}`);

        if (isLoadMore) {
            posts.value = [...posts.value, ...result.data];
        } else {
            posts.value = result.data;
        }

        hasNextPage.value = result.meta.current_page < result.meta.last_page;
    } catch (e) {
        error.value = e.message;
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const loadMore = () => {
    if (hasNextPage.value && !loadingMore.value) {
        currentPage.value++;
        fetchNews(true);
    }
};

const handleHeadlineScroll = () => {
    const el = headlinesContainerRef.value;
    if (el) {
        const atRightEdge = el.scrollLeft + el.clientWidth >= el.scrollWidth - 50;
        if (atRightEdge) {
            loadMore();
        }
    }
};

watch(headlinesContainerRef, (newEl, oldEl) => {
    if (oldEl) {
        oldEl.removeEventListener('scroll', handleHeadlineScroll);
    }
    if (newEl) {
        newEl.addEventListener('scroll', handleHeadlineScroll);
    }
});

watch(
    posts,
    () => {
        setupObserver();

        if (posts.value.length > 0 && activeHeadline.value === null) {
            activeHeadline.value = Object.keys(groupedPosts.value)[0];
        }

        scrollAndExpand();
    },
    { deep: true, flush: 'post' }
);

watch(loadMoreTrigger, (newTrigger) => {
    if (scrollObserver) {
        scrollObserver.disconnect();
    }

    if (newTrigger) {
        scrollObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMore();
            }
        });
        scrollObserver.observe(newTrigger);
    }
}, { flush: 'post' });

onMounted(() => {
    fetchNews();
});

onUnmounted(() => {
    if (observer) {
        observer.disconnect();
    }
    if (scrollObserver) {
        scrollObserver.disconnect();
    }
    if (headlinesContainerRef.value) {
        headlinesContainerRef.value.removeEventListener('scroll', handleHeadlineScroll);
    }
});
</script>

<template>
    <div class="space-y-4">
        <div v-if="posts.length > 0" class="sticky top-20 z-20 bg-white/80 backdrop-blur-md -mx-4 px-4">
            <div class="relative">
                <div ref="headlinesContainerRef"
                    class="flex items-center space-x-4 overflow-x-auto py-3 scrollbar-hide">
                    <button v-for="date in Object.keys(groupedPosts)" :key="date"
                        :ref="(el) => setHeadlineButtonRef(el, date)"
                        class="text-sm font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-colors duration-300"
                        :class="[activeHeadline === date ? 'bg-blue text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']"
                        @click="scrollToDate(date)">
                        {{ date }}
                    </button>
                    <div v-if="loadingMore" class="flex items-center justify-center p-2 pr-8">
                        <svg class="animate-spin h-5 w-5 text-blue" xmlns="http://www.w3.org/2000/svg" fill="none"
                            viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                </div>
                <div
                    class="absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" />
            </div>
        </div>

        <div v-if="loading && !posts.length" class="space-y-4">
            <BreakingNewsSkeleton v-for="n in 5" :key="n" />
        </div>

        <div v-else-if="error" class="text-center text-red-500 py-10">
            <p>{{ $t('error_no_items_found') }}</p>
        </div>

        <div v-else-if="posts.length === 0" class="text-center text-gray-500 py-10">
            <p>{{ $t('no_articles_founds') }}</p>
        </div>

        <div v-else class="relative flex flex-col gap-4">
            <div class="-left-7 absolute top-0 bottom-0 bg-blue w-0.5" />
            <div class="-left-7 absolute top-0 from-white via-white/50 to-blue bg-gradient-to-b w-0.5 h-24" />
            <div class="-left-7 absolute bottom-0 from-white via-white/50 to-blue bg-gradient-to-t w-0.5 h-24" />
            <div v-for="(dayPosts, date) in groupedPosts" :key="date" class="space-y-4">
                <h2 :ref="(el) => setHeadlineRef(el, date)" :data-date="date"
                    class="text-lg font-semibold sticky top-24 mt-1 rounded-md px-2 p-1 z-10 w-auto transition-colors duration-300 inline-flex"
                    :class="[activeHeadline === date ? 'bg-blue text-white' : 'bg-gradient-to-b from-white-light to-white']">
                    {{ date }}
                </h2>
                <BreakingNewsItem v-for="item in dayPosts" :key="item.id" :ref="(el) => setItemRef(el, item.id)"
                    :is-expanded="item.id === expandedItemId && item.postTags?.data?.some((item) => item.name === 'BreakingNews')"
                    :post="item" @toggle="expandedItemId = item.id === expandedItemId ? null : item.id" />
            </div>
        </div>

        <div v-if="loadingMore" class="space-y-4">
            <BreakingNewsSkeleton v-for="n in 3" :key="n" />
        </div>
        <div v-if="hasNextPage" ref="loadMoreTrigger" class="h-10" />
    </div>
</template>
